/* eslint-disable max-lines */
import { isEmpty } from "@swiggy-private/js-utils";
import { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { ICardsEntity } from "@swiggy-private/dls-react-mxn/dist/WidgetController/type";
import type { AppliedFilters } from "@swiggy-private/portal-dineout-context";
import type { IRefineWidget } from "@swiggy-private/portal-dineout-ui";
import { CustomWidgetTypes } from "@swiggy-private/portal-dineout-ui";

import { ICategoryItems, INestedItemCategoryType, WIDGET_TYPES } from "./types";

/**
 * Builds a Set of all selected facet value ids from applied filters.
 */
const buildSelectedIdSet = (appliedFilters: AppliedFilters): Set<string> => {
    const selectedIdSet = new Set<string>();
    const single = appliedFilters.singleSelect || {};
    const multi = appliedFilters.multiSelect || {};
    for (const id of Object.values(single)) {
        if (id) selectedIdSet.add(id);
    }
    for (const arr of Object.values(multi)) {
        if (Array.isArray(arr)) {
            for (const id of arr) {
                if (id) selectedIdSet.add(id);
            }
        }
    }
    return selectedIdSet;
};

/**
 * Transforms InlineViewFilterSortWidget facet selections using the provided predicate.
 * isSelected should return true when a facet value id must be selected.
 * Pure transformation (does not mutate input).
 */
const transformInlineFacetSelections = (
    cards: IPLWidgetControllerCards[],
    isSelected: (facetValueId: string | undefined) => boolean,
) => {
    return cards.map((c) => {
        const card = c?.card?.card as unknown as IRefineWidget;
        if (!card || card["@type"] !== CustomWidgetTypes.REFINE_WIDGET) return c;

        const newCard = { ...card };

        if (Array.isArray(newCard.facetList)) {
            newCard.facetList = newCard.facetList.map((facet) => {
                const facetInfo = facet?.facetInfo;
                if (!Array.isArray(facetInfo)) return facet;
                const updatedFacetInfo = facetInfo.map((fi) => {
                    const fiId = fi?.id as string | undefined;
                    return { ...fi, selected: isSelected(fiId) };
                });

                return { ...facet, facetInfo: updatedFacetInfo };
            });
        }

        return { ...c, card: { ...c.card, card: newCard } };
    });
};

/**
 * Resets all inline filter selections to false for every facet and value.
 * Pure transformation (does not mutate input).
 */
export const resetInlineFacetSelections = (cards: IPLWidgetControllerCards[]) => {
    return transformInlineFacetSelections(cards, () => false);
};

/**
 * Applies the selections from the provided appliedFilters to the inline widget.
 * For each facet value, sets selected=true if its id exists in appliedFilters; otherwise false.
 * Pure transformation (does not mutate input).
 */
export const applyAppliedFiltersToInlineWidget = (
    cards: IPLWidgetControllerCards[],
    appliedFilters: AppliedFilters,
) => {
    const selectedIdSet = buildSelectedIdSet(appliedFilters);
    return transformInlineFacetSelections(cards, (id) => !!(id && selectedIdSet.has(id)));
};

//TODO: make this function more efficient and more readable
/**
 * Prunes menu cards by a dish-level predicate, removing empty categories/cards.
 * Pure transformation (does not mutate input).
 */
const pruneCardsByDishPredicate = (
    cards: IPLWidgetControllerCards[],
    predicate: (dish: ICardsEntity) => boolean,
): IPLWidgetControllerCards[] => {
    const prunedCards = cards
        .map((c) => {
            const inner = c?.card?.card as unknown as IRefineWidget;
            if (!inner || typeof inner !== "object") return c;
            const type = inner["@type"] as string;
            if (type === WIDGET_TYPES.ITEM_CATEGORY_TYPE) {
                const itemCards = (inner as { itemCards?: ICategoryItems[] }).itemCards || [];
                const kept = itemCards.filter((ic) => {
                    const dish = ic?.card;
                    return !!dish && dish["@type"] === WIDGET_TYPES.DISH && predicate(dish as unknown as ICardsEntity);
                });
                if (kept.length === 0) return null;
                return { ...c, card: { ...c.card, card: { ...inner, itemCards: kept } } };
            }
            if (type === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE) {
                const categories = (inner as unknown as INestedItemCategoryType).categories || [];
                const newCategories = categories
                    .map((cat) => {
                        const kept = (cat.itemCards || []).filter((ic) => {
                            const dish = ic?.card;
                            return (
                                !!dish &&
                                dish["@type"] === WIDGET_TYPES.DISH &&
                                predicate(dish as unknown as ICardsEntity)
                            );
                        });
                        if (kept.length === 0) return null;
                        return { ...cat, itemCards: kept };
                    })
                    .filter(Boolean);
                if (newCategories.length === 0) return null;
                return { ...c, card: { ...c.card, card: { ...inner, categories: newCategories } } };
            }
            return c;
        })
        .filter(Boolean) as IPLWidgetControllerCards[];

    return prunedCards;
};

/**
 * Filters menu cards by applied filters using AND across filter types and OR within multi-select values.
 * The check is done against dish.card.info.applicableFacets.facetIds (array of strings).
 * - A dish matches if, for every filterType present in appliedFilters, the dish facetIds intersect
 *   with that filterType's selected ids (singleSelect as one id; multiSelect as set of ids).
 * - Prunes empty categories and cards.
 */
export const filterCardsByAppliedFilters = (
    cards: IPLWidgetControllerCards[],
    applied?: AppliedFilters,
): IPLWidgetControllerCards[] => {
    if (isEmpty(applied)) return cards;

    // Fast path: no filters -> return original
    const hasSingle = Object.keys(applied.singleSelect || {}).length > 0;
    const hasMulti = Object.keys(applied.multiSelect || {}).length > 0;
    if (!hasSingle && !hasMulti) return cards;

    // Precompute a map filterType -> Set(ids)
    const filterTypeToIds = new Map<string, Set<string>>();
    for (const [k, v] of Object.entries(applied.singleSelect || {})) {
        if (v) filterTypeToIds.set(k, new Set<string>([v]));
    }
    for (const [k, arr] of Object.entries(applied.multiSelect || {})) {
        if (Array.isArray(arr) && arr.length) filterTypeToIds.set(k, new Set<string>(arr));
    }

    const matchesAllFilters = (facetIds: string[] | undefined): boolean => {
        if (!facetIds || facetIds.length === 0) return false;
        for (const ids of filterTypeToIds.values()) {
            // OR within a type: at least one of the type ids should be present in dish facetIds
            let typeMatch = false;
            for (const id of ids) {
                if (facetIds.includes(id)) {
                    typeMatch = true;
                    break;
                }
            }
            if (!typeMatch) return false; // AND across types
        }
        return true;
    };

    const prunedCards = pruneCardsByDishPredicate(cards, (dish) => {
        const applicable = (dish as { applicableFacets?: { facetIds?: string[] } }).applicableFacets;
        const facetIds = applicable?.facetIds || [];
        return matchesAllFilters(facetIds);
    });

    const updatedFilterPills = applyAppliedFiltersToInlineWidget(prunedCards, applied);

    return updatedFilterPills as IPLWidgetControllerCards[];
};

/**
 * Builds a map of facetId -> selection type ("SELECT_TYPE_SINGLESELECT" | "SELECT_TYPE_MULTISELECT")
 * by scanning InlineViewFilterSortWidget cards.
 */
export const buildFacetSelectionMap = (cards: IPLWidgetControllerCards[]): Map<string, string> => {
    const facetIdToSelection = new Map<string, string>();
    cards.forEach((c) => {
        const inner = c?.card?.card as unknown as IRefineWidget;

        if (!inner || typeof inner !== "object") return;
        if (inner["@type"] !== CustomWidgetTypes.REFINE_WIDGET) return;

        const facetList = inner.facetList || [];
        facetList.forEach((facet) => {
            if (facet?.id) {
                facetIdToSelection.set(facet.id, facet.selection || "");
            }
        });
    });

    return facetIdToSelection;
};
